import { Space, Tag, message } from "antd";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import {
  getPredictions,
  createPrediction,
  updatePrediction,
  publishPrediction,
  deletePrediction,
} from "../apis/prediction";
import PredictionForm from "../components/prediction/PredictionForm";
import PredictionDetails from "../components/prediction/PredictionDetails";
import CommonTable from "../components/common/CommonTable";
import CutOffText from "../components/common/CutOffText";
import { useLoader } from "../components/common/Loader/LoaderProvider";
import ActionsModal from "../components/common/Modals/ActionsModal";
import TableAction from "../components/common/TableAction";
import CommonButton from "../components/common/Button/CommonButton";
import CustomLayout from "../components/layout/CustomLayout";
import withAuth from "../routeProtector/withAuth";
import useDocumentTitle from "../utils/useDocumentTitle";

const VIEW_LIST = "list";
const VIEW_CREATE = "create";
const VIEW_EDIT = "edit";
const VIEW_DETAIL = "detail";

const statusFilterOptions = [
  { value: "all", label: "All Status" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

const Predictions = () => {
  const dispatch = useDispatch();
  const [predictions, setPredictions] = useState([]);
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortedField, setSortedField] = useState("createdAt");

  const [currentView, setCurrentView] = useState(VIEW_LIST);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [deleteData, setDeleteData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [publishModal, setPublishModal] = useState(false);
  const [publishData, setPublishData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishingFromForm, setPublishingFromForm] = useState(false);

  const { showLoader, hideLoader } = useLoader();
  useDocumentTitle("Predictions");

  const debounceTimer = useRef(null);

  const handleSearchChange = useCallback((value) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 500);
  }, []);

  const fetchPredictions = async () => {
    showLoader();
    try {
      const res = await dispatch(
        getPredictions({
          page: currentPage,
          limit,
          searchQuery: debouncedSearch.trim(),
          status: statusFilter,
          sortBy: sortedField,
          sortOrder,
        })
      );
      if (res.status) {
        const data = res.data?.data;
        setPredictions(data?.predictions || []);
        setTotalItems(data?.totalPredictions || 0);
        setCurrentPage(data?.currentPage || 1);
      }
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (currentView === VIEW_LIST) {
      fetchPredictions();
    }
    // eslint-disable-next-line
  }, [limit, currentPage, debouncedSearch, sortedField, sortOrder, statusFilter, currentView]);

  const handleSort = (field) => {
    if (sortedField === field) {
      setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSortedField(field);
      setSortOrder("asc");
    }
  };

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const res = await dispatch(createPrediction(payload));
      if (res.status) {
        message.success("Prediction created as draft successfully");
        setCurrentPage(1);
        setCurrentView(VIEW_LIST);
      } else {
        message.error(res.message || "Failed to create prediction");
      }
    } catch (err) {
      message.error("Failed to create prediction");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndPublish = async (payload) => {
    setPublishingFromForm(true);
    try {
      const createRes = await dispatch(createPrediction(payload));
      if (createRes.status) {
        const newId = createRes.data?.data?._id;
        if (newId) {
          const pubRes = await dispatch(publishPrediction(newId));
          if (pubRes.status) {
            message.success("Prediction created and published successfully");
            setCurrentPage(1);
            setCurrentView(VIEW_LIST);
          } else {
            message.warning(
              "Prediction created as draft but failed to publish: " +
                (pubRes.message || "")
            );
            setCurrentPage(1);
            setCurrentView(VIEW_LIST);
          }
        }
      } else {
        message.error(createRes.message || "Failed to create prediction");
      }
    } catch (err) {
      message.error("Failed to create and publish prediction");
    } finally {
      setPublishingFromForm(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!selectedPrediction?._id) return;
    setSaving(true);
    try {
      const res = await dispatch(
        updatePrediction(selectedPrediction._id, payload)
      );
      if (res.status) {
        message.success("Prediction updated successfully");
        setCurrentView(VIEW_LIST);
        setSelectedPrediction(null);
      } else {
        message.error(res.message || "Failed to update prediction");
      }
    } catch (err) {
      message.error("Failed to update prediction");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!publishData?._id) return;
    showLoader();
    try {
      const res = await dispatch(publishPrediction(publishData._id));
      if (res.status) {
        message.success("Prediction published successfully");
        setPublishModal(false);
        setPublishData(null);
        setSelectedPrediction(null);
        setCurrentView(VIEW_LIST);
        fetchPredictions();
      } else {
        message.error(res.message || "Failed to publish prediction");
      }
    } catch (err) {
      message.error("Failed to publish prediction");
    } finally {
      hideLoader();
    }
  };

  const handleDelete = async () => {
    if (!deleteData?._id) return;
    showLoader();
    try {
      const res = await dispatch(deletePrediction(deleteData._id));
      if (res.status) {
        message.success("Prediction deleted successfully");
        setDeleteModal(false);
        setDeleteData(null);
        fetchPredictions();
      } else {
        message.error(res.message || "Failed to delete prediction");
      }
    } catch (err) {
      message.error("Failed to delete prediction");
    } finally {
      hideLoader();
    }
  };

  const columns = [
    {
      title: "Question",
      dataIndex: "question",
      key: "question",
      sorter: true,
      onHeaderCell: () => ({ onClick: () => handleSort("question") }),
      render: (text) => <CutOffText maxLength={40} value={text} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={status === "published" ? "green" : "orange"}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      width: 180,
      render: (tags) =>
        tags?.length > 0
          ? tags.map((tag) => (
              <Tag key={tag} color="blue" className="mb-1">
                {tag}
              </Tag>
            ))
          : "-",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      sorter: true,
      onHeaderCell: () => ({ onClick: () => handleSort("end_date") }),
      width: 180,
      render: (date) => {
        const isExpired = dayjs(date).isBefore(dayjs());
        return (
          <span className={isExpired ? "text-red-500" : ""}>
            {dayjs(date).format("MMM DD, YYYY")}
            {isExpired && " (Expired)"}
          </span>
        );
      },
    },
    {
      title: "Participants",
      dataIndex: "participant_count",
      key: "participant_count",
      width: 120,
      render: (count) => count || 0,
    },
    {
      title: "Action",
      key: "action",
      width: 180,
      render: (_, record) => (
        <Space>
          <TableAction
            view={true}
            onView={() => {
              setSelectedPrediction(record);
              setCurrentView(VIEW_DETAIL);
            }}
            edit={record.status === "draft"}
            onEdit={() => {
              setSelectedPrediction(record);
              setCurrentView(VIEW_EDIT);
            }}
            deleteU={record.status === "draft"}
            onDelete={() => {
              setDeleteData(record);
              setDeleteModal(true);
            }}
            traceData={true}
          />
          {record.status === "draft" && (
            <CommonButton
              type="primary"
              onClick={() => {
                setPublishData(record);
                setPublishModal(true);
              }}
              className="rounded-md text-xs bg-[#4f46e5] hover:bg-[#4338ca]"
              size="small"
            >
              Publish
            </CommonButton>
          )}
        </Space>
      ),
    },
  ];

  const handlePageChange = (page) => setCurrentPage(page);
  const handleSizeChange = (_, size) => setLimit(size);

  const renderContent = () => {
    switch (currentView) {
      case VIEW_CREATE:
        return (
          <PredictionForm
            onSubmit={handleCreate}
            onCancel={() => setCurrentView(VIEW_LIST)}
            onPublish={handleCreateAndPublish}
            loading={saving}
            publishing={publishingFromForm}
          />
        );
      case VIEW_EDIT:
        return (
          <PredictionForm
            onSubmit={handleUpdate}
            onCancel={() => {
              setCurrentView(VIEW_LIST);
              setSelectedPrediction(null);
            }}
            onPublish={async (payload) => {
              setPublishingFromForm(true);
              try {
                const updateRes = await dispatch(
                  updatePrediction(selectedPrediction._id, payload)
                );
                if (updateRes.status) {
                  const pubRes = await dispatch(
                    publishPrediction(selectedPrediction._id)
                  );
                  if (pubRes.status) {
                    message.success("Prediction updated and published successfully");
                    setSelectedPrediction(null);
                    setCurrentView(VIEW_LIST);
                  } else {
                    message.warning(
                      "Updated but failed to publish: " + (pubRes.message || "")
                    );
                  }
                } else {
                  message.error(updateRes.message || "Failed to update prediction");
                }
              } catch (err) {
                message.error("Failed to update and publish prediction");
              } finally {
                setPublishingFromForm(false);
              }
            }}
            initialData={selectedPrediction}
            loading={saving}
            publishing={publishingFromForm}
          />
        );
      case VIEW_DETAIL:
        return (
          <PredictionDetails
            predictionId={selectedPrediction?._id}
            onBack={() => {
              setCurrentView(VIEW_LIST);
              setSelectedPrediction(null);
            }}
          />
        );
      default:
        return (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold font-poppins m-0">
                Predictions
              </h2>
              <CommonButton
                type="primary"
                onClick={() => setCurrentView(VIEW_CREATE)}
                className="rounded-md bg-[#4f46e5] hover:bg-[#4338ca]"
              >
                + Create Prediction
              </CommonButton>
            </div>
            <CommonTable
              apiData={predictions}
              columns={columns}
              currentPage={currentPage}
              itemsPerPage={limit}
              onPageChange={handlePageChange}
              onShowSizeChange={handleSizeChange}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              onSearchChange={handleSearchChange}
              totalItems={totalItems}
              filter={true}
              showCSV={false}
              additionalFilters={[
                {
                  defaultValue: statusFilter,
                  value: statusFilter,
                  placeholder: "Filter by Status",
                  onChange: (value) => {
                    setStatusFilter(value);
                    setCurrentPage(1);
                  },
                  options: statusFilterOptions,
                },
              ]}
            />

            {deleteModal && (
              <ActionsModal
                title=""
                type="delete"
                isOpen={deleteModal}
                onCancel={() => setDeleteModal(false)}
                onOk={handleDelete}
                footer={false}
                centered={true}
                closable={false}
                maskClosable={false}
                className="delete-modal"
                cancelBtnClass="cancleBtnAction"
                cancelBtnClick={() => {
                  setDeleteModal(false);
                  setDeleteData(null);
                }}
                cancelButtonProps="Cancel"
                saveBtnClass="saveBtnAction"
                saveBtnClick={handleDelete}
                saveButtonProps="Delete"
                description="Do you really want to delete this prediction? This action cannot be undone."
                mainTitle="Are you sure?"
              />
            )}

            {publishModal && (
              <ActionsModal
                title=""
                type="save"
                isOpen={publishModal}
                onCancel={() => setPublishModal(false)}
                onOk={handlePublish}
                footer={false}
                centered={true}
                closable={false}
                maskClosable={false}
                className="delete-modal"
                cancelBtnClass="cancleBtnAction !text-gray-700 !border-gray-300 !bg-gray-200 hover:!bg-gray-300"
                cancelBtnClick={() => {
                  setPublishModal(false);
                  setPublishData(null);
                }}
                cancelButtonProps="Cancel"
                saveBtnClass="saveBtnAction !bg-[#16a34a] hover:!bg-[#15803d] !border-[#16a34a]"
                saveBtnClick={handlePublish}
                saveButtonProps="Publish"
                savebtnType={false}
                description="Once published, this prediction will be visible to users and cannot be edited or deleted."
                mainTitle="Publish Prediction?"
              />
            )}
          </>
        );
    }
  };

  return <CustomLayout>{renderContent()}</CustomLayout>;
};

export default withAuth(Predictions);
